import { motion } from 'framer-motion';
import { ChevronsUpDown, MoveRight, UserCog, Users } from 'lucide-react';
import React from 'react';
import { RoleOption } from '.';
import UserBadgeItem from '../Badge/user-badge-item';
import Button from '../Button';
import Card from '../Card';
import CopyButton from '../CopyButton';
import DropdownMenu from '../Dropdown';
import Label from '../Label';
import MultiSelect, { Option } from '../MultiSelect';
import Textarea from '../Textarea';
import Tooltip from '../Tooltip';
import { InviteTeamNote } from '../UpgradeLockModal/user-invite-note';
import SelectTeamMemberItem from './custom-select-team-member';

type TeamMemberProps = {
    users: Option[],
    handleMultiSelectOnChange: (options: Option[]) => void,
    multiSelectValue: Option[] | undefined,
    handleChangeShareNote: React.ChangeEventHandler<HTMLTextAreaElement> | undefined,
    shareNote: string | number | readonly string[] | undefined,
    userRoles: RoleOption[],
    roleValue: Option,
    handleSelectRole: (roleValue: Option) => void,
    handleSubmitTeamMembers: () => void,
    inviteTeamLink: string,
    isInvitingMember: boolean,
    websiteUserRole?: string,
    inviteMoreStatus?: boolean,
    userCount: number | string | undefined,
    teamPrice: string | number | undefined,
    startingCheckout: boolean,
    basePrice?: string | number,
    additionalSeatPrice?: string,
    currentInterval?: string,
    plan?: string,
}

const MotionCard = motion(Card);

const TeamMembers = ({
    users,
    handleMultiSelectOnChange,
    multiSelectValue,
    handleChangeShareNote,
    shareNote,
    userRoles,
    roleValue,
    handleSelectRole,
    handleSubmitTeamMembers,
    inviteTeamLink,
    isInvitingMember,
    websiteUserRole,
    inviteMoreStatus,
    userCount,
    teamPrice,
    startingCheckout,
    basePrice,
    additionalSeatPrice,
    currentInterval,
    plan,
}: TeamMemberProps) => {

    return (
        <MotionCard
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-4 border-none p-0 shadow-none">
            <Card.Content className="flex flex-col gap-2 w-full space-y-4 p-0">
                <div className='text-sm'>
                    Invited as core <strong>team members </strong>for enhanced collaboration features and full access to <strong>additional dashboard areas.</strong>
                </div>
                <div className='flex flex-col gap-2'>
                    <Label>Add Team Members</Label>
                    <div className="relative flex w-full items-center gap-2">
                        <MultiSelect
                            portal
                            creatable={websiteUserRole === 'admin'}
                            options={users}
                            placeholder="Type a name or email to add…"
                            onChange={handleMultiSelectOnChange}
                            value={multiSelectValue}
                            hideClearAllButton
                            emptyIndicator={<div className='text-sm text-muted pointer-events-auto'>No team members found.</div>}
                            customBadgeComponent={UserBadgeItem}
                            customItemComponent={SelectTeamMemberItem}
                            className='rounded-lg'
                            rightSection={
                                websiteUserRole === 'admin' &&
                                <DropdownMenu data-atarim={true}>
                                    <DropdownMenu.Trigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="hover:bg-transparent self-start"
                                            tooltip="Select a role for the new teammates"
                                            tooltipSide='bottom'
                                            tooltipAlign="end"
                                        >
                                            {roleValue.label}<ChevronsUpDown size={16} />
                                        </Button>
                                    </DropdownMenu.Trigger>
                                    <DropdownMenu.Content align="start" className="min-w-[14rem]" portal container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper') || document.body}>
                                        <DropdownMenu.Label>New Teammates are:</DropdownMenu.Label>
                                        <DropdownMenu.Separator />
                                        {
                                            userRoles.map((role) => {
                                                return (
                                                    <Tooltip content={role.value === 'admin' ? 'Can access everything' : 'Access to assigned projects and limited Workspace features'} portal>
                                                        <DropdownMenu.CheckboxItem
                                                            onClick={() => handleSelectRole(role)}
                                                            checked={roleValue.value === role.value}
                                                        >
                                                            {role.value === 'admin' ? <UserCog size={16} /> : <Users size={16} />} {role.label}
                                                        </DropdownMenu.CheckboxItem>
                                                    </Tooltip>
                                                );
                                            })
                                        }
                                    </DropdownMenu.Content>
                                </DropdownMenu>
                            }
                        />
                    </div>
                </div>
                {inviteMoreStatus && (
                    <InviteTeamNote
                        userCount={userCount}
                        basePrice={basePrice}
                        additionalSeatPrice={additionalSeatPrice}
                        currentInterval={currentInterval}
                        plan={plan}
                        teamPrice={teamPrice}
                    />
                )}
                <div className='flex flex-col gap-2'>
                    <Label>Add a Note</Label>
                    <Textarea className="placeholder:text-muted-foreground rounded-lg" placeholder="Provide some context for your invite…" value={shareNote} onChange={handleChangeShareNote} />
                </div>
                <div className="flex justify-between gap-2 pb-4">
                    <CopyButton
                        text={inviteTeamLink}
                        variant='outline'
                        size='sm'
                        buttonText="Team Invite Link"
                        tooltip='Share this link with team members to join your Workspace'
                        tooltipSide='top'
                        tooltipAlign="start"
                    />
                    <Button
                        onClick={handleSubmitTeamMembers}
                        variant="primary"
                        size="sm"
                        loading={isInvitingMember || startingCheckout}
                    >
                        Invite My Team
                        <MoveRight size={18} />
                    </Button>
                </div>
            </Card.Content>
        </MotionCard>
    );
};

export default TeamMembers;